import { Box, Button, Modal } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import * as yup from 'yup';
import { fetchBatchesDropdown } from '@Features/dropdowns/dropdownsSlice';
import { useTypedSelector } from '@Features/store';
import { useSnackbar } from '@Providers/useSnackbar';
import { updateScratchcardsByBatch } from '../Services/ScratchCards.req';
import { scratchCardsGetAll } from '../Slices/ScratchCards.slice';
import { batchOptionLabel } from './batchOptionLabel';
import { CardTargetChip, CardTargetType } from './CardTarget';
import {
	CheckboxStyled,
	Container,
	ButtonsContainer,
	SelectStyled,
	TitleStyled,
} from './GenerateCode';
import TargetModePicker, {
	buildTargetPayload,
	GenerationMode,
	TARGET_SCHEMA_FIELDS,
} from './TargetModePicker';

type Props = {
	handleModelClose: () => void;
	openModel: boolean;
	classRooms: any;
};

const SCHEMA = yup.object().shape({
	...TARGET_SCHEMA_FIELDS,
	batch_number: yup.number().required(),
	checker: yup.boolean(),
});

/**
 * Retargets every UNUSED card in a batch at once through the shared mode
 * picker. Used cards are skipped by the backend — their targets record what
 * was bought — and the response's skip count is surfaced in the snackbar.
 */
const BulkEditModule: React.FC<Props> = ({
	handleModelClose,
	openModel,
	classRooms,
}) => {
	const dispatch = useDispatch();
	const {
		formState: { isValid, isSubmitting },
		watch,
		reset,
		control,
		handleSubmit,
		setValue,
	} = useForm<any>({
		resolver: yupResolver(SCHEMA),
		mode: 'onTouched',
		defaultValues: {
			checker: false,
			classroom_id: null,
			course_id: null,
			instructor_id: null,
			value: null,
			standalone_course_id: null,
			bundle_id: null,
		},
	});

	const { dropdownBatches: batches } = useTypedSelector(
		(state) => state.dropdowns
	);
	const { dateRange, selectedFilters, query, sortBy, meta } = useTypedSelector(
		(state) => state.ScratchCards
	);
	const { displaySnackbar } = useSnackbar();

	const mode: GenerationMode = watch('mode');

	const selectedBatchNumber = watch('batch_number');
	const selectedBatch = batches.find(
		(batchOption) => batchOption.batch === selectedBatchNumber
	);

	const onSubmitForm = async (formData: Record<string, any>) => {
		try {
			const { data } = await updateScratchcardsByBatch({
				batch_number: formData.batch_number,
				...buildTargetPayload(mode, formData),
			});
			const { updated, skipped_used } = data?.data ?? {};
			displaySnackbar(
				'success',
				`${updated ?? 0} codes retargeted${
					skipped_used ? `, ${skipped_used} used codes skipped` : ''
				}`
			);
			dispatch(
				scratchCardsGetAll({
					page: meta?.current_page ?? 1,
					query: query ?? '',
					sortBy,
					filters: selectedFilters,
					perPage: meta?.per_page ?? 10,
					from: dateRange.from,
					to: dateRange.to,
					export: false,
				})
			);
			dispatch(fetchBatchesDropdown());
			closeAndReset();
		} catch (error: any) {
			displaySnackbar(
				'error',
				error?.response?.data?.message ||
					error?.message ||
					'Unable to update batch'
			);
		}
	};

	const closeAndReset = () => {
		reset();
		handleModelClose();
	};

	return (
		<Modal
			open={openModel}
			onClose={closeAndReset}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description"
		>
			<Container>
				<form onSubmit={handleSubmit(onSubmitForm)}>
					<Box sx={{ marginLeft: '1.5rem', marginTop: '2.25rem' }}>
						<TitleStyled id="modal-modal-title">
							Edit Codes <GreySpan>(by batch, unused only)</GreySpan>
						</TitleStyled>
					</Box>

					<Box
						sx={{
							marginLeft: '3.5rem',
							marginRight: '4rem',
							marginTop: '1.5rem',
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<Controller
							control={control}
							name="batch_number"
							render={({ field }) => (
								<FormControl
									size="small"
									sx={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}
								>
									<InputLabel id="bulk-edit-batch-label">
										Batch Number
									</InputLabel>
									<SelectStyled
										labelId="bulk-edit-batch-label"
										id="bulk-edit-batch-select"
										{...field}
										value={field.value ?? ''}
										label="Batch Number"
									>
										{batches.map((batchOption) => (
											<MenuItem
												key={batchOption.batch}
												value={batchOption.batch}
											>
												{batchOptionLabel(batchOption)}
											</MenuItem>
										))}
									</SelectStyled>
								</FormControl>
							)}
						/>

						{selectedBatch?.target_type && (
							<BatchInfo>
								<span>Current:</span>
								<CardTargetChip
									target={selectedBatch.target_type as CardTargetType}
								/>
								<span>{selectedBatch.target_label ?? '—'}</span>
								{selectedBatch.value !== null &&
									selectedBatch.value !== undefined && (
										<span>· value {selectedBatch.value}</span>
									)}
								{selectedBatch.total !== undefined && (
									<span>
										· {selectedBatch.total} codes
										{selectedBatch.used
											? ` (${selectedBatch.used} used — kept as-is)`
											: ''}
									</span>
								)}
							</BatchInfo>
						)}

						<TargetModePicker
							control={control}
							setValue={setValue}
							watch={watch}
							classRooms={classRooms}
							active={openModel}
						/>

						<Controller
							control={control}
							name="checker"
							render={({ field }) => (
								<CheckboxStyled
									control={<Checkbox />}
									{...field}
									checked={!!field.value}
									label="All selected options are correct"
								/>
							)}
						/>
					</Box>
					<ButtonsContainer>
						<Button
							variant="text"
							color="inherit"
							sx={{
								fontFamily: 'Roboto',
								fontSize: '14px',
								padding: '6px 16px',
								color: '#6c63ff',
								backgroundColor: '#fff',
								outline: 'none',
							}}
							disabled={!isValid || !watch('checker') || isSubmitting}
							type="submit"
						>
							Save
						</Button>
						<Button
							variant="text"
							color="warning"
							sx={{
								fontFamily: 'Roboto',
								fontSize: '14px',
								padding: '6px 16px',
								color: '#ff9800',
								outline: 'none',
								backgroundColor: '#fff',
							}}
							onClick={closeAndReset}
						>
							Cancel
						</Button>
					</ButtonsContainer>
				</form>
			</Container>
		</Modal>
	);
};

export default BulkEditModule;

const GreySpan = styled.span`
	font-family: Roboto;
	font-size: 1rem;
	color: rgba(0, 0, 0, 0.14);
`;

const BatchInfo = styled.div`
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 0.5rem;
	margin: 0.25rem 0 0.75rem;
	font-family: Roboto;
	font-size: 0.875rem;
	color: rgba(0, 0, 0, 0.6);
`;
